import React, { useContext, useMemo } from 'react';
import AbsenceBlock from '../Absences/AbsenceBlock';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import BlockIcon from '@mui/icons-material/Block';
import { PaperWrapper } from 'atoms/PaperWrapper/PaperWrapper';
import { store } from '../store';
import { getPresenceWeek } from './api';
import { setPresence } from './api';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { format, addDays, startOfDay } from 'date-fns';
import { IconButton } from '@mui/material';
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const STATUS_CONFIG = {
  'unavailable': { label: 'Unavailable', color: '#999898', icon: <BlockIcon fontSize="small" /> },
  'on-site': { label: 'On-Site', color: '#1866b8', icon: <LocationOnIcon fontSize="small" /> },
  'off-site': { label: 'Off-Site', color: '#2e7d32', icon: <LocationOffIcon fontSize="small" /> }
};

const WeekGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
  gap: 6,
  marginTop: 8
});

const shouldForwardProp = (prop) => prop !== 'isToday';

const DayColumn = styled('div', { shouldForwardProp })(({ isToday }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  padding: '8px 4px',
  borderRadius: 8,
  backgroundColor: isToday ? '#f0f7f0' : 'transparent',
  border: isToday ? '1px solid #4caf50' : '1px solid #eee'
}));

const DayLabel = styled(Typography)({
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  color: '#666'
});

export default function WeekPlanner() {
  const { state } = useContext(store);
  const queryClient = useQueryClient();
  const siteId = state.site?._id;
  const userId = state.user?._id;

  const today = startOfDay(new Date());
  const [weekStart, setWeekStart] = React.useState(today);
  const days = useMemo(() =>
    [...Array(7)].map((_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const fromDate = format(days[0], 'yyyy-MM-dd');
  const toDate = format(days[6], 'yyyy-MM-dd');

  const { data: weekData, isLoading } = useQuery({
    queryKey: ['presenceWeek', siteId, userId, fromDate, toDate],
    queryFn: () => getPresenceWeek(siteId, fromDate, toDate),
    enabled: !!siteId,
    refetchOnWindowFocus: false
  });

  const myWeekPresence = useMemo(() => {
    const map = {};
    (weekData?.data || []).forEach(entry => {
      const entryUserId = entry.userId?._id || entry.userId;
      if (entryUserId === userId) map[entry.date] = entry;
    });
    return map;
  }, [weekData, userId]);

  const [draftNotes, setDraftNotes] = React.useState({});
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [editingDate, setEditingDate] = React.useState(null);
  const [savingDate, setSavingDate] = React.useState(null);
  const [saveError, setSaveError] = React.useState(null);
  const weekGridRef = React.useRef(null);
  const plannerId = React.useId();

  React.useEffect(() => {
    const handleOutsidePress = event => {
      const grid = weekGridRef.current;
      if (!grid) return;
      const day = event.target.closest?.('[data-presence-day]');
      if (day && grid.contains(day)) return;
      const menu = event.target.closest?.('[data-presence-menu]');
      if (menu?.getAttribute('data-presence-menu') === plannerId) return;

      // MUI's menu backdrop covers the dates. A click over a date can close
      // the menu without leaving that date, even though its DOM target is outside.
      if (event.target.closest?.('.MuiBackdrop-root')) {
        const overDay = [...grid.querySelectorAll('[data-presence-day]')].some(element => {
          const bounds = element.getBoundingClientRect();
          return bounds.width > 0 && bounds.height > 0 &&
            event.clientX >= bounds.left && event.clientX <= bounds.right &&
            event.clientY >= bounds.top && event.clientY <= bounds.bottom;
        });
        if (overDay) return;
      }
      setSelectedDate(null);
    };
    // Opening a Select on mouse-down can retarget the subsequent click to body.
    // Classify the press before its popup changes what's under the pointer.
    document.addEventListener('pointerdown', handleOutsidePress, true);
    return () => document.removeEventListener('pointerdown', handleOutsidePress, true);
  }, [plannerId]);

  React.useEffect(() => {
    setDraftNotes({});
    setSelectedDate(null);
    setEditingDate(null);
    setSaveError(null);
  }, [siteId, userId]);

  const saveDay = async (date, status, note) => {
    if (savingDate || !siteId || state.user?.role === 'site-console') return;
    setSavingDate(date);
    setSaveError(null);
    try {
      await setPresence(siteId, {date, status, note});
      await Promise.all([
        queryClient.invalidateQueries(['presenceWeek', siteId]),
        queryClient.invalidateQueries(['presenceHistory', siteId]),
        queryClient.invalidateQueries(['presence', siteId])
      ]);
      setDraftNotes(previous => {
        const next = {...previous};
        delete next[date];
        return next;
      });
      setEditingDate(previous => previous === date ? null : previous);
    } catch (error) {
      setSaveError(date);
    } finally {
      setSavingDate(null);
    }
  };

  const isTodayDate = (date) => format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
  const isPast = (date) => date < today;

  const selectDay = date => {
    if (savingDate) return;
    setSelectedDate(date);
    setEditingDate(previous => previous === date ? previous : null);
  };

  // Arrow button handlers
  const handlePrevWeek = () => {
    const prevWeekStart = addDays(weekStart, -7);
    if (prevWeekStart >= today) {
      setWeekStart(prevWeekStart);
      setSelectedDate(null);
      setEditingDate(null);
    }
  };
  const handleNextWeek = () => {
    setWeekStart(addDays(weekStart, 7));
    setSelectedDate(null);
    setEditingDate(null);
  };

  return (
    <PaperWrapper heading={''} loading={isLoading}>
      <div style={{display:'flex', alignItems:'center', marginBottom:16, marginTop:-4}}>
        <span style={{fontWeight:600, fontSize: '0.75rem', color: '#555', textTransform: 'uppercase', marginRight:12}}>My Schedule</span>
        <IconButton
          onClick={handlePrevWeek}
          disabled={weekStart <= today || !!savingDate}
          aria-label="Previous schedule week"
          size="small"
          variant="outlined"
          sx={{marginRight:2}}
        >
          <NavigateBeforeIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={handleNextWeek}
          disabled={!!savingDate}
          aria-label="Next schedule week"
          size="small"
          variant="outlined"
          sx={{minWidth:32}}
        >
          <NavigateNextIcon fontSize="small" />
        </IconButton>
        <span style={{marginLeft:12, fontWeight:600, fontSize: '0.7rem'}}>
          {format(days[0], 'd MMM')} - {format(days[6], 'd MMM')}
        </span>
      </div>
      {!isLoading && (
        <WeekGrid ref={weekGridRef}>
          {days.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const entry = myWeekPresence[dateStr];
            const currentStatus = entry?.status || 'unavailable';
            const note = draftNotes[dateStr] ?? entry?.note ?? '';
            const noteChanged = note !== (entry?.note || '');
            const dayLabel = format(day, 'EEEE do MMMM');
            const dayIsPast = isPast(day) && !isTodayDate(day);
            const statusColor = STATUS_CONFIG[currentStatus]?.color || '#999898';

            return (
              <DayColumn key={dateStr} data-presence-day={dateStr} isToday={isTodayDate(day)}
                role="group" aria-label={`Schedule for ${dayLabel}`}
                tabIndex={dayIsPast ? -1 : 0}
                onClick={event => {
                  if (!dayIsPast && event.currentTarget.contains(event.target)) selectDay(dateStr);
                }}
                onKeyDown={event => {
                  if (!dayIsPast && event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) {
                    event.preventDefault();
                    selectDay(dateStr);
                  }
                }}>
                <DayLabel>{format(day, 'EEE do')}</DayLabel>
                <Select
                  value={currentStatus}
                  onOpen={() => selectDay(dateStr)}
                  MenuProps={{slotProps: {paper: {'data-presence-menu': plannerId}}}}
                  onChange={(e) => saveDay(dateStr, e.target.value, note)}
                  inputProps={{'aria-label': `Status for ${dayLabel}`}}
                  disabled={dayIsPast || !!savingDate}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.7rem',
                    minWidth: 0,
                    width: '100%',
                    color: statusColor,
                    fontWeight: 600,
                    '.MuiOutlinedInput-notchedOutline': { borderColor: statusColor },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: statusColor },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: statusColor }
                  }}
                >
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <MenuItem key={key} value={key} sx={{ fontSize: '0.75rem', gap: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: cfg.color }}>
                        {cfg.icon} {cfg.label}
                      </div>
                    </MenuItem>
                  ))}
                </Select>
                {(entry?.absences || []).map((absence, index) => <AbsenceBlock key={`${absence._id}-${index}`} absence={absence} />)}
                {editingDate === dateStr ? <>
                  <TextField
                    label="Note"
                    placeholder="Add note..."
                    slotProps={{inputLabel: {shrink: true}}}
                    sx={{mt: 1.5}}
                    value={note}
                    onChange={event => setDraftNotes(previous => ({...previous, [dateStr]: event.target.value}))}
                    inputProps={{'aria-label': `Note for ${dayLabel}`}}
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={4}
                    fullWidth
                    autoFocus
                    disabled={dayIsPast || !!savingDate}
                    error={saveError === dateStr}
                    helperText={saveError === dateStr ? 'Unable to save. Please try again.' : ''}
                  />
                  {noteChanged && <Button size="small" disabled={dayIsPast || !!savingDate}
                    aria-label={`Save note for ${dayLabel}`}
                    onClick={() => saveDay(dateStr, currentStatus, note)}>
                    {savingDate === dateStr ? 'Saving…' : 'Save note'}
                  </Button>}
                  <Button size="small" disabled={!!savingDate} onClick={() => {
                    setDraftNotes(previous => {
                      const next = {...previous};
                      delete next[dateStr];
                      return next;
                    });
                    setSaveError(null);
                    setEditingDate(null);
                  }}>Cancel</Button>
                </> : (entry?.note || selectedDate === dateStr) && <Button
                  size="small"
                  aria-label={`${entry?.note ? 'Edit' : 'Add'} note for ${dayLabel}`}
                  disabled={dayIsPast || !!savingDate}
                  onClick={() => { setEditingDate(dateStr); setSaveError(null); }}
                  sx={{mt: 1, fontSize: '0.75rem', fontWeight: 400, textTransform: 'none',
                    color: 'text.secondary', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                    textAlign: 'left', justifyContent: 'flex-start', width: '100%'}}
                >{entry?.note || 'Add note'}</Button>}
                {editingDate !== dateStr && saveError === dateStr &&
                  <Typography role="alert" variant="caption" color="error">Unable to save. Please try again.</Typography>}
              </DayColumn>
            );
          })}
        </WeekGrid>
      )}
    </PaperWrapper>
  );
}
